import type { RegistryItemFragment } from '@graphql/fragments/registry.graphql.generated';
import { useIsInternationalRegistryRoute } from '@apps/registry/common/hooks/useIsInternationalRegistryRoute';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useDisclosure } from '@withjoy/joykit';
import { useEffect, useState } from 'react';
import { createRegistryItemSubject } from '../../createRegistryItemSubject';
import { setOpenRecentlyAddedDrawer } from './recentlyAddedDrawerTrigger';

export const useDrawerVisibility = () => {
  const [currentItem, setCurrentItem] = useState<RegistryItemFragment | null>(null);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const isInternationalRegistryRoute = useIsInternationalRegistryRoute();

  const openDrawerImmediately = useEventCallback((item?: RegistryItemFragment) => {
    setCurrentItem(item ?? null);
    onOpen();
  });

  const closeDrawer = useEventCallback(() => {
    setCurrentItem(null);
    onClose();
  });

  useEffect(() => {
    const sub = createRegistryItemSubject.subscribe(data => {
      if (!data.purchaseMethod) {
        setCurrentItem(data.item);
      }
    });

    return () => {
      sub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (isInternationalRegistryRoute) {
      setOpenRecentlyAddedDrawer(null);
      return;
    }

    setOpenRecentlyAddedDrawer(openDrawerImmediately);
    return () => {
      setOpenRecentlyAddedDrawer(null);
    };
  }, [openDrawerImmediately, isInternationalRegistryRoute]);

  return {
    currentItem,
    isOpen,
    isAddingItem: isOpen && !currentItem,
    onClose: closeDrawer,
  };
};
